import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FileUploadZone, { fileKey, type PerFileMetadataConfig } from './FileUploadZone';
import type { UploadStatus } from '../types';

export interface PerFileOutcome {
  fileKey: string;
  status: 'success' | 'failure';
  message?: string;
}

type SubmitResult = void | PerFileOutcome[];

interface UploadDocumentsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    files: File[],
    metadataValues?: Record<string, string>,
  ) => Promise<SubmitResult> | SubmitResult;
  title?: string;
  maxFileSize?: number;
  maxFiles?: number;
  maxTotalSize?: number;
  acceptedTypes?: readonly string[];
  /** When provided, each staged-file row renders a metadata Select. */
  perFileMetadata?: PerFileMetadataConfig;
  /** When provided, a single batch-level Select renders between the drop
   *  zone and the staged-file list. Mutually exclusive with `perFileMetadata`
   *  in practice. */
  batchMetadata?: PerFileMetadataConfig;
}

const UploadDocumentsModal = React.memo(function UploadDocumentsModal({
  open,
  onClose,
  onSubmit,
  title = 'Upload Documents',
  maxFileSize,
  maxFiles,
  maxTotalSize,
  acceptedTypes,
  perFileMetadata,
  batchMetadata,
}: UploadDocumentsModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [metadataValues, setMetadataValues] = useState<Record<string, string>>({});
  const [batchValue, setBatchValue] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Map<string, number>>(new Map());
  const [uploadStatus, setUploadStatus] = useState<Map<string, UploadStatus>>(new Map());
  const [error, setError] = useState<string | undefined>();
  const [showOutcomes, setShowOutcomes] = useState(false);

  const resetAll = useCallback(() => {
    setFiles([]);
    setMetadataValues({});
    setBatchValue('');
    setUploadProgress(new Map());
    setUploadStatus(new Map());
    setError(undefined);
    setShowOutcomes(false);
  }, []);

  const handleFilesChange = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
    setError(undefined);
    // Drop metadata for files no longer staged
    setMetadataValues((prev) => {
      const stillStaged = new Set(newFiles.map(fileKey));
      const next: Record<string, string> = {};
      for (const k of Object.keys(prev)) {
        if (stillStaged.has(k)) next[k] = prev[k];
      }
      return next;
    });
  }, []);

  const handleMetadataChange = useCallback((key: string, value: string) => {
    setMetadataValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    resetAll();
    onClose();
  }, [isUploading, onClose, resetAll]);

  const handleSubmit = useCallback(async () => {
    if (files.length === 0) {
      setError('Please select at least one file to upload.');
      return;
    }

    setIsUploading(true);
    setError(undefined);
    setShowOutcomes(false);

    // Spec FR-026 calls for a single overall progress indicator (the LinearProgress
    // under DialogContent), not per-file. Clear any stale per-file status/progress
    // from a previous attempt; the legacy path below will populate fresh ones on
    // resolution, and the per-file outcome path sets only success/failure indicators.
    const newStatus = new Map<string, UploadStatus>();
    const newProgress = new Map<string, number>();
    setUploadStatus(newStatus);
    setUploadProgress(newProgress);

    try {
      const submitMetadata = batchMetadata
        ? { [batchMetadata.key]: batchValue }
        : metadataValues;
      const result = await onSubmit(files, submitMetadata);

      if (Array.isArray(result)) {
        // Per-file outcome path
        const outcomeStatus = new Map<string, UploadStatus>();
        for (const o of result) {
          outcomeStatus.set(o.fileKey, o.status === 'success' ? 'completed' : 'failed');
        }
        setUploadStatus(outcomeStatus);

        const anyFailure = result.some((o) => o.status === 'failure');
        if (!anyFailure) {
          // Auto-close on all-success
          resetAll();
          onClose();
        } else {
          setShowOutcomes(true);
        }
      } else {
        // Legacy path: assume all completed
        files.forEach((f) => {
          const key = fileKey(f);
          newStatus.set(key, 'completed');
          newProgress.set(key, 100);
        });
        setUploadStatus(new Map(newStatus));
        setUploadProgress(new Map(newProgress));
        setTimeout(() => {
          handleClose();
        }, 500);
      }
    } catch (err) {
      files.forEach((f) => {
        const key = fileKey(f);
        newStatus.set(key, 'failed');
      });
      setUploadStatus(new Map(newStatus));
      // Prefer the server's user-facing message when it surfaces one — axios
      // errors expose it on `response.data.message` (shared ClaimOS
      // ErrorResponse shape). Fall back to the JS Error's `.message`, then to
      // a generic. Keeps the modal's inline error useful for 4xx/5xx paths
      // where the backend mapped a clear reason (e.g. "edit window expired").
      const ax = err as { response?: { data?: { message?: string } } };
      const serverMessage = ax?.response?.data?.message;
      setError(
        serverMessage ??
          (err instanceof Error ? err.message : undefined) ??
          'Upload failed. Please try again.',
      );
    } finally {
      setIsUploading(false);
    }
  }, [files, metadataValues, batchValue, batchMetadata, onSubmit, handleClose, onClose, resetAll]);

  const hasFiles = files.length > 0;
  const allPerFileMetadataSet =
    !perFileMetadata?.required ||
    files.every((f) => Boolean(metadataValues[fileKey(f)]));
  const batchMetadataSet = !batchMetadata?.required || Boolean(batchValue);
  const submitDisabled =
    !hasFiles || isUploading || !allPerFileMetadataSet || !batchMetadataSet;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 1 } } }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontWeight: 500,
          fontSize: 18,
          color: 'text.primary',
          pb: 1,
        }}
      >
        {title}
        <IconButton
          onClick={handleClose}
          disabled={isUploading}
          size="small"
          aria-label="Close dialog"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <FileUploadZone
          files={files}
          onChange={handleFilesChange}
          maxFileSize={maxFileSize}
          maxFiles={maxFiles}
          maxTotalSize={maxTotalSize}
          acceptedTypes={acceptedTypes}
          uploadProgress={uploadProgress}
          uploadStatus={uploadStatus}
          error={error}
          disabled={isUploading}
          label=""
          perFileMetadata={perFileMetadata}
          metadataValues={metadataValues}
          onMetadataChange={handleMetadataChange}
          stagedFilesSlotBefore={
            batchMetadata ? (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    display: 'block',
                    mb: 0.5,
                    fontWeight: 500,
                    fontSize: 14,
                    color: 'text.primary',
                  }}
                >
                  {batchMetadata.label}:
                </Typography>
                <FormControl size="small" fullWidth>
                  <Select
                    value={batchValue}
                    onChange={(e) => setBatchValue(String(e.target.value))}
                    disabled={isUploading}
                    displayEmpty
                    inputProps={{ 'aria-label': batchMetadata.label }}
                    sx={{
                      height: 36,
                      fontSize: 13,
                      bgcolor: 'background.paper',
                      borderRadius: '4px',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
                    }}
                  >
                    <MenuItem value="" sx={{ fontSize: 13, color: 'text.secondary' }}>
                      {`Select ${batchMetadata.label}`}
                    </MenuItem>
                    {batchMetadata.options.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            ) : null
          }
        />
        {isUploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Box sx={{ display: 'flex', gap: 1.5, width: '100%', justifyContent: 'flex-end' }}>
          {showOutcomes ? (
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isUploading}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitDisabled}
                startIcon={
                  isUploading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <FileUploadOutlinedIcon />
                  )
                }
                sx={{
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
});

export default UploadDocumentsModal;
